import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ModalController, ToastController } from "@ionic/angular";
import {
  PaymentMethod,
  PaymentMethodType,
  PaymentMethodTypeLabels,
} from "../../../../models/payment-method.model";
import { PaymentMethodService } from "../../../services/payment-method.service";
import { AuthService } from "../../../services/auth.service";

@Component({
    selector: "app-payment-method-modal",
    templateUrl: "./payment-method-modal.component.html",
    styleUrls: ["./payment-method-modal.component.scss"],
    standalone: false
})
export class PaymentMethodModalComponent implements OnInit {
  @Input() isEdit = false;
  @Input() isView = false;
  @Input() paymentMethod?: PaymentMethod;

  form: FormGroup;
  saving = false;
  selectedMethodType: PaymentMethodType = "Zelle";

  paymentMethodTypes: PaymentMethodType[] = [
    "Zelle",
    "ContaBancaria",
    "ContaInternacional",
  ];
  paymentMethodTypeLabels = PaymentMethodTypeLabels;

  constructor(
    private formBuilder: FormBuilder,
    private modalController: ModalController,
    private toastController: ToastController,
    private paymentMethodService: PaymentMethodService,
    private authService: AuthService
  ) {
    this.form = this.createForm();
  }

  ngOnInit() {
    if (this.paymentMethod) {
      this.selectedMethodType = this.paymentMethod.metodoSelecionado || "Zelle";
      this.populateForm();
    }

    if (this.isView) {
      this.form.disable();
    }
  }

  createForm(): FormGroup {
    return this.formBuilder.group({
      // Personal Information
      ssn: [""],
      taxId: [""],

      // Zelle fields
      zelleFullName: [""],
      zelleEmailOrPhone: [""],
      zelleBankName: [""],

      // Bank Account fields
      bankAccountBankName: [""],
      bankAccountHolderName: [""],
      bankAccountBankCode: [""],
      bankAccountNumber: [""],

      // International Account fields
      intlAccountBankName: [""],
      intlAccountSwiftCode: [""],
      intlAccountNumber: [""],
      intlAccountIban: [""],
      intlAccountHolderName: [""],
    });
  }

  populateForm() {
    if (!this.paymentMethod) return;

    const method = this.paymentMethod;

    // Populate personal information
    this.form.patchValue({
      ssn: method.ssn,
      taxId: method.taxId,
    });

    if (method.zelle) {
      this.form.patchValue({
        zelleFullName: method.zelle.nomeCompleto,
        zelleEmailOrPhone: method.zelle.emailOuTelefone,
        zelleBankName: method.zelle.nomeBanco,
      });
    }

    if (method.contaBancaria) {
      this.form.patchValue({
        bankAccountBankName: method.contaBancaria.nomeBanco,
        bankAccountHolderName: method.contaBancaria.titularConta,
        bankAccountBankCode: method.contaBancaria.codigoBanco,
        bankAccountNumber: method.contaBancaria.numeroConta,
      });
    }

    if (method.contaInternacional) {
      this.form.patchValue({
        intlAccountBankName: method.contaInternacional.nomeBanco,
        intlAccountSwiftCode: method.contaInternacional.codigoSwift,
        intlAccountNumber: method.contaInternacional.numeroConta,
        intlAccountIban: method.contaInternacional.iban,
        intlAccountHolderName: method.contaInternacional.titularConta,
      });
    }
  }

  onMethodTypeChange() {
    this.clearValidators();
    this.setValidatorsForCurrentMethod();
  }

  clearValidators() {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.clearValidators();
      this.form.get(key)?.updateValueAndValidity();
    });
  }

  setValidatorsForCurrentMethod() {
    switch (this.selectedMethodType) {
      case "Zelle":
        this.form.get("zelleFullName")?.setValidators([Validators.required]);
        this.form
          .get("zelleEmailOrPhone")
          ?.setValidators([Validators.required]);
        this.form.get("zelleBankName")?.setValidators([Validators.required]);
        break;
      case "ContaBancaria":
        this.form
          .get("bankAccountBankName")
          ?.setValidators([Validators.required]);
        this.form
          .get("bankAccountHolderName")
          ?.setValidators([Validators.required]);
        this.form
          .get("bankAccountBankCode")
          ?.setValidators([Validators.required]);
        this.form
          .get("bankAccountNumber")
          ?.setValidators([Validators.required]);
        break;
      case "ContaInternacional":
        this.form
          .get("intlAccountBankName")
          ?.setValidators([Validators.required]);
        this.form
          .get("intlAccountSwiftCode")
          ?.setValidators([Validators.required]);
        this.form
          .get("intlAccountNumber")
          ?.setValidators([Validators.required]);
        this.form
          .get("intlAccountHolderName")
          ?.setValidators([Validators.required]);
        break;
    }

    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.updateValueAndValidity();
    });
  }

  buildPaymentMethodData(): PaymentMethod {
    const formValue = this.form.value;
    const currentUser = this.authService.user;

    const paymentMethod: PaymentMethod = {
      id: this.paymentMethod?.id,
      motoristaId: currentUser?.id || "",
      ssn: formValue.ssn,
      taxId: formValue.taxId,
      metodoSelecionado: this.selectedMethodType,
    };

    switch (this.selectedMethodType) {
      case "Zelle":
        paymentMethod.zelle = {
          selecionado: true,
          nomeCompleto: formValue.zelleFullName,
          emailOuTelefone: formValue.zelleEmailOrPhone,
          nomeBanco: formValue.zelleBankName,
        };
        break;
      case "ContaBancaria":
        paymentMethod.contaBancaria = {
          selecionado: true,
          nomeBanco: formValue.bankAccountBankName,
          titularConta: formValue.bankAccountHolderName,
          codigoBanco: formValue.bankAccountBankCode,
          numeroConta: formValue.bankAccountNumber,
        };
        break;
      case "ContaInternacional":
        paymentMethod.contaInternacional = {
          selecionado: true,
          nomeBanco: formValue.intlAccountBankName,
          codigoSwift: formValue.intlAccountSwiftCode,
          numeroConta: formValue.intlAccountNumber,
          iban: formValue.intlAccountIban,
          titularConta: formValue.intlAccountHolderName,
        };
        break;
    }

    return paymentMethod;
  }

  async save() {
    this.setValidatorsForCurrentMethod();

    if (this.form.invalid) {
      await this.showToast("Please fill in all required fields", "warning");
      return;
    }

    this.saving = true;
    const paymentMethodData = this.buildPaymentMethodData();

    const operation =
      this.isEdit && this.paymentMethod?.id
        ? this.paymentMethodService.updatePaymentMethod(
            this.paymentMethod.id,
            paymentMethodData
          )
        : this.paymentMethodService.createPaymentMethod(paymentMethodData);

    operation.subscribe({
      next: async () => {
        await this.showToast(
          this.isEdit
            ? "Payout method updated successfully"
            : "Payout method created successfully",
          "success"
        );
        this.modalController.dismiss({ saved: true });
      },
      error: async (error) => {
        console.error("Error saving payout method:", error);
        await this.showToast(
          "Failed to save payout method. Please try again.",
          "danger"
        );
        this.saving = false;
      },
    });
  }

  async showToast(message: string, color: string) {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color,
      position: "top",
    });
    await toast.present();
  }

  dismiss() {
    this.modalController.dismiss();
  }

  get modalTitle(): string {
    if (this.isView) return "Payout Method Details";
    return this.isEdit ? "Edit Payout Method" : "Add Payout Method";
  }
}
